import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Play, ImageIcon } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { PageHero } from "./About";
import { ScrollReveal } from "../components/ScrollReveal";

const TABS = ["All", "Clinic", "Posters", "Videos"];

export default function Gallery() {
  const { data: items = [] } = useQuery({ queryKey: ["gallery"], queryFn: async () => (await api.get("/gallery")).data });
  const [tab, setTab] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const filtered = useMemo(() => {
    if (tab === "All") return items;
    if (tab === "Videos") return items.filter((i) => i.media_type === "video");
    return items.filter((i) => i.category === tab);
  }, [items, tab]);

  return (
    <div>
      <PageHero eyebrow="Gallery" title="Revive Gallery" subtitle="A look inside our clinic, facilities, team and community health initiatives." />
      <section className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap gap-2 justify-center mb-12">
            {TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`gallery-tab-${t.toLowerCase()}`} className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${tab === t ? "gradient-bg text-white shadow-lg" : "bg-white border border-slate-200 text-[#334E68] hover:border-[#1A75BC]"}`}>
                {t}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20 text-[#627D98]">
              <ImageIcon size={48} className="mx-auto mb-4 opacity-40" />
              <p>No media in this category yet. Check back soon!</p>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
              {filtered.map((it, i) => (
                <ScrollReveal key={it.id} delay={(i % 6) * 0.05}>
                  <button onClick={() => setLightbox(it)} className="group relative w-full rounded-2xl overflow-hidden card-lift block" data-testid={`gallery-item-${i}`}>
                    {it.media_type === "video" ? (
                      <div className="relative bg-black">
                        <video src={fileUrl(it.url)} className="w-full object-cover" muted />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30"><div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center"><Play size={24} className="text-[#1A75BC] ml-1" /></div></div>
                      </div>
                    ) : (
                      <img src={fileUrl(it.url)} alt={it.title} className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    )}
                    {(it.title || it.category) && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4 text-left">
                        <div className="text-white font-bold text-sm">{it.title}</div>
                        <div className="text-white/70 text-xs">{it.category}</div>
                      </div>
                    )}
                  </button>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button className="absolute top-5 right-5 text-white" onClick={() => setLightbox(null)}><X size={28} /></button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {lightbox.media_type === "video" ? (
              <video src={fileUrl(lightbox.url)} controls autoPlay className="w-full max-h-[80vh] rounded-2xl" />
            ) : (
              <img src={fileUrl(lightbox.url)} alt={lightbox.title} className="w-full max-h-[80vh] object-contain rounded-2xl" />
            )}
            {lightbox.title && <div className="text-white text-center mt-4"><div className="font-bold">{lightbox.title}</div><div className="text-sm text-white/60">{lightbox.description}</div></div>}
          </div>
        </div>
      )}
    </div>
  );
}
